import React, { useEffect, useMemo } from "react";
import { postFeatureSelectMessage } from "../../utils/message.helper";
import "./KeyNavigationModel.css";
import { t } from "i18next";
import { Feature, Features } from "../../constants/features";
import { useKeyboardShortcuts } from "../../hooks";
import { BaseOption } from "../../types/options";

interface KeyNavigationOption extends BaseOption {
  key: string;
}

interface KeyNavigationModelProps {
  onClose: () => void;
  children: React.ReactNode;
}

const gridItems: KeyNavigationOption[] = [
  {
    id: Features.NAVIGATION_PAGE_STRUCTURE,
    label: "page_structure",
    key: "P",
  },
  {
    id: Features.CONTENT_BIGGER_TEXT,
    label: "bigger_text",
    key: "B",
  },
  {
    id: Features.COLOR_SMART_CONTRAST,
    label: "smart_contrast",
    key: "C",
  },
  {
    id: Features.CONTENT_DYSLEXIC_FRIENDLY,
    label: "dyslexic_friendly",
    key: "D",
  },
  {
    id: Features.CONTENT_TEXT_SPACING_LIGHT,
    label: "text_spacing",
    key: "E",
  },
  {
    id: Features.NAVIGATION_READING_GUIDE,
    label: "reading_guide",
    key: "F",
  },
  {
    id: Features.NAVIGATION_MUTE_SOUND,
    label: "mute_sound",
    key: "G",
  },
  {
    id: Features.NAVIGATION_HIDE_IMAGES,
    label: "hide_images",
    key: "I",
  },
  {
    id: Features.COLOR_CONTRAST_MODE,
    label: "contrast_mode",
    key: "H",
  },
  {
    id: Features.CONTENT_LINE_HEIGHT_1_5X,
    label: "line_height(1.5x)",
    key: "J",
  },
  {
    id: Features.NAVIGATION_DICTIONARY,
    label: "dictionary",
    key: "K",
  },
  {
    id: Features.CONTENT_HIGHLIGHT_LINKS,
    label: "highlight_links",
    key: "L",
  },
  {
    id: Features.CONTENT_ALIGN_CENTER,
    label: "align_center",
    key: "M",
  },
  {
    id: Features.NAVIGATION_KEY_NAVIGATION,
    label: "key_navigation",
    key: "N",
  },
  {
    id: Features.CONTENT_ALIGN_RIGHT,
    label: "align_right",
    key: "O",
  },
  {
    id: Features.NAVIGATION_PAUSE_ANIMATION,
    label: "pause_animation",
    key: "P",
  },
  { id: Features.NAVIGATION_RESET, label: "reset", key: "R" },
  {
    id: Features.NAVIGATION_SCREEN_READER,
    label: "screen_reader",
    key: "S",
  },
  {
    id: Features.NAVIGATION_DARK_CURSOR,
    label: "dark_cursor",
    key: "T",
  },
  {
    id: Features.NAVIGATION_BIG_CURSOR,
    label: "big_cursor",
    key: "U",
  },
  {
    id: Features.NAVIGATION_VOICE_NAVIGATION,
    label: "voice_navigation",
    key: "V",
  },
  {
    id: Features.NAVIGATION_READING_MASK,
    label: "reading_mask",
    key: "W",
  },
  {
    id: Features.COLOR_CONTRAST_MODE,
    label: "contrast_+",
    key: "X",
  },
  {
    id: Features.COLOR_LOW_SATURATION,
    label: "saturation",
    key: "Y",
  },
  {
    id: Features.CONTENT_ALIGN_LEFT,
    label: "align_left",
    key: "Q",
  },
  {
    id: Features.NAVIGATION_HIDE_INTERFACE,
    label: "hide_interface",
    key: "H",
  },
  {
    id: Features.NAVIGATION_SHOW_INTERFACE,
    label: "show_interface",
    key: "S",
  },
  { id: Features.PROFILE_BLIND, label: "blind", key: "0" },
  {
    id: Features.PROFILE_LOW_VISION,
    label: "low_vision",
    key: "2",
  },
  {
    id: Features.PROFILE_COLOR_BLIND,
    label: "color_blind",
    key: "3",
  },
  {
    id: Features.PROFILE_SEIZURE_EPILEPTIC,
    label: "seizure",
    key: "4",
  },
  { id: Features.PROFILE_ADHD, label: "adhd", key: "5" },
  {
    id: Features.PROFILE_MOTOR_IMPAIRED,
    label: "motor",
    key: "6",
  },
  {
    id: Features.PROFILE_COGNITIVE_LEARNING,
    label: "cognitive",
    key: "7",
  },
  {
    id: Features.PROFILE_DYSLEXIA,
    label: "dyslexia",
    key: "8",
  },
];

const KeyNavigationModel: React.FC<KeyNavigationModelProps> = ({
  onClose,
  children,
}) => {
  const shortcuts = useMemo(() => {
    return gridItems.map((item) => ({
      key: item.key,
      altKey: true,
      ctrlKey: true,
      callback: () => {
        postFeatureSelectMessage(item.id as Feature);
      },
    }));
  }, []);

  useKeyboardShortcuts(shortcuts);

  const isMac = navigator.platform.toLowerCase().includes("mac");
  const displayItems = gridItems.map((item) => ({
    ...item,
    key: `${isMac ? "Option" : "Alt"} + Ctrl + ${item.key}`,
  }));

  return (
    <div className="modal-overlay">
      <div className="modal-content rounded">
        {children}
        <div className="modal-body">
          <p className="text-sm text-start my-2 px-2">
            {t(
              "use_the_keyboard_shortcuts_below_to_activate_accessibility_features"
            )}
          </p>
        </div>
        <div className="flex flex-col my-2 px-2 gap-2 overflow-y-auto max-h-96 thin-scrollbar">
          {displayItems.map((item, index) => (
            <div
              key={index}
              className="flex justify-between px-2 py-2 shadow-md"
            >
              <p className="text-sm font-semibold">
                {item.label ? t(item.label) : (item.id as string)}
              </p>
              <span className="text-sm text-blue-600 border border-blue-600 rounded px-3 py-1">
                {item.key}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default KeyNavigationModel;
